/**
 * CRM nav/route permission aliases → real CRM `module.*` keys.
 *
 * The CRM route guards (apps/crm) AND the shell CRM sidebar (apps/gateway) both
 * gate on a readable, coarse `crm.<resource>.<action>` alias. But the CRM
 * permission map — served by GET /crm/users/me/permissions — is keyed by the
 * FINE-GRAINED `module.*` catalog. The two namespaces are distinct, so checking
 * a `crm.*` alias directly against the map always fails and the row/route is
 * hidden. This map bridges them; it lives here (a shared package both apps
 * depend on) so route access and nav visibility resolve identically.
 *
 * Each alias resolves to the real `module.*` key(s) that grant the surface.
 * Multiple keys are ANY-of (e.g. "view leads" = own | team | all). An empty list
 * means no backend key backs the surface → treat as ungated.
 *
 * Keep in sync with the CRM `module.*` catalog (apps/crm/src/lib/permission-keys
 * + backend uiPermissions.constant).
 */
export const CRM_NAV_PERMISSION_ALIASES: Record<string, readonly string[]> = {
  'crm.company.read': ['module.company.view'],
  'crm.contact.read': ['module.contact.view'],
  'crm.lead.read': ['module.lead.view.own', 'module.lead.view.team', 'module.lead.view.all'],
  'crm.deal.read': ['module.deal.view.own', 'module.deal.view.team', 'module.deal.view.all'],
  'crm.task.read': ['module.task.view.own', 'module.task.view.team', 'module.task.view.all'],
  'crm.product.read': ['module.product.view'],
  'crm.approval.read': ['module.approval.view'],
  'crm.import.read': ['module.import.view'],
  'crm.subscription.read': ['module.subscription.view'],
  'crm.subscription.write': ['module.subscription.renewal.manage', 'module.subscription.edit'],
  'crm.report.read': ['module.report.view'],
  'crm.commission.read': [
    'module.commission.statement.view',
    'module.commission.leaderboard.view',
    'module.commission.structure.view',
  ],
  'crm.commission.write': ['module.commission.structure.manage'],
  'crm.dsr.read': ['module.dpdp.dsr.view'],
  'crm.audit.read': ['module.audit.view'],
  // SPDI Backfill + Approval Analytics each own a key that used to enforce
  // NOTHING — both surfaces were gated on a coarser neighbour (audit.view and
  // report.view respectively), leaving their real key as a dead toggle in the
  // matrix. Wired here. Verified no-op for access: in all four seeded templates
  // the coarse key and the specific key are granted to exactly the same set.
  'crm.spdi.read': ['module.spdi.view'],
  'crm.approvalanalytics.read': ['module.approval.analytics.view'],
  // Recovery (hard-delete restore) — its own module keys, gated in Compliance.
  // Seeing the Recycle Bin requires `view` specifically — `restore` is the right
  // to put a record BACK, which should not by itself grant sight of the bin.
  // (ANY-of semantics meant listing both let `.restore` alone open the page.)
  'crm.recovery.read': ['module.recovery.view'],
  // Breach incidents — its own key group (view | manage | report), not audit.
  'crm.breach.read': ['module.breach.view', 'module.breach.manage', 'module.breach.report'],
  'crm.user.read': ['module.user.view'],
  // Settings surfaces — each maps to its OWN real key, NOT one shared
  // "settings.write" (which conflated Roles, Data Protection, and Auto-task).
  'crm.settings.read': ['module.settings.view', 'module.masterconfig.view'],
  'crm.settings.write': ['module.settings.manage.general', 'module.settings.manage.config'],
  // Master Config — its OWN view/manage keys so the Master rail icon and the
  // master-config page gate on `module.masterconfig.*`, not the generic
  // system-settings keys (which conflated it with Roles/Data Protection).
  'crm.masterconfig.read': ['module.masterconfig.view'],
  'crm.masterconfig.write': ['module.masterconfig.manage'],
  'crm.rbac.read': ['module.rbac.view'],
  // Settings → Notifications master (catalogue + per-workspace channel toggles).
  'crm.notificationconfig.read': ['module.notification_config.view'],
  'crm.notificationconfig.write': ['module.notification_config.edit'],
  // Calling (VoIP) — module 27.
  //
  // The CALL aliases are ANY-of across the three view scopes: the widest one the
  // caller holds decides what the API then returns, so a rep with only
  // `view.own` must still reach the page.
  //
  // The CONFIG aliases are separate and admin-only: provider credentials and the
  // caller ID are a tenant-level surface, not something a manager configures.
  'crm.call.read': [
    'module.call.view.own',
    'module.call.view.team',
    'module.call.view.all',
  ],
  'crm.call.write': ['module.call.edit.own'],
  'crm.call.make': ['module.call.make'],
  'crm.voipconfig.read': ['module.voip.config.view'],
  'crm.voipconfig.write': ['module.voip.config.manage'],
  'crm.rbac.write': ['module.rbac.template.manage'],
  // Settings → Objects & Fields — its own object-definition key, not generic settings.
  'crm.objects.read': ['module.objectdef.view', 'module.objectdef.manage'],
  // Settings → Approvals (matrix + TAT config) — approval-admin keys, NOT the
  // CRM approvals INBOX (`crm.approval.read` → module.approval.view).
  'crm.approvalconfig.read': [
    'module.approval_matrix.view',
    'module.approval_matrix.manage',
    'module.approval_tat.view',
    'module.approval_tat.manage',
    'module.approval.config.manage',
  ],
  // Data Protection PAGE (PII Fields + Retention + Grievance tabs). Split from
  // Consent Notices so each surface gates on the key its backend READ actually
  // requires. Any-of the tabs a user can read: PII Fields / Retention read via
  // retention.manage; Grievance reads via consent.view. The page then gates each
  // TAB individually (see DataProtectionPage) so a visible page never 403s on load.
  'crm.dataprotection.read': [
    'module.dpdp.retention.manage',
    'module.dpdp.consent.view',
  ],
  // Consent Notices admin page — its backend GET requires consent.view, so it no
  // longer shares the Data Protection alias (which let a retention-only manager
  // open a page that 403s, and a consent viewer see an Edit button that 403s).
  'crm.consentnotices.read': ['module.dpdp.consent.view'],
  'crm.autotask.write': ['module.task.trigger.manage'],
  'crm.goal.read': ['module.goal.view'],
  // Custom (admin-created) objects — generic record-level view key.
  'crm.customobject.read': ['module.customobject.view'],
  // Notifications are per-user (you only ever see your own; the backend routes
  // are self-scoped on req.user). Gate on the universal baseline key
  // `module.dashboard.view` — present in EVERY template (see crm-api
  // permissions.constant `USER_READ`) — so the surface is DELIBERATELY gated to
  // "any user with baseline CRM access" rather than silently ungated via an empty
  // list. If an admin ever builds a template without dashboard access, the
  // notifications route/rail correctly fail-close for that user.
  'crm.notification.read': ['module.dashboard.view'],
};

/**
 * Resolve a CRM nav/route alias to the real `module.*` key(s) that grant it.
 *  - Known alias → its mapped keys (possibly empty = ungated).
 *  - Unknown alias → returned as-is (so a real `module.*` key passed directly
 *    still works, and unmapped conventions like `crm.o.<api>.read` pass through).
 */
export function resolveCrmNavPermission(alias: string): readonly string[] {
  return CRM_NAV_PERMISSION_ALIASES[alias] ?? [alias];
}
